/*
 * Copyright (c) 2021. BrightNight. All rights reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */
import z from 'zod';
import { objectKeys } from '../utils';
import { capexSpendScheduleListSchema } from './capex-spend-schedule';
import { compoundVersionIdSchemaZ } from './components';
import { formNewConfigPayload, } from './config';
import { componentIdSchema } from './core';
import { genericCostSchema, getDiscretionaryCostSchema, hvCostingSchema, } from './hv-costing';
export const dcCostCategoryLabelMap = {
    modules: 'Modules',
    racking: 'Racking',
    inverters: 'Inverters',
    site_construction: 'Site Construction',
    foundations: 'Foundations',
    trackers: 'Trackers',
    monitoring: 'Monitoring',
    dc_collection: 'DC Collection',
    // backward compatibility
    site_prep_fencing_etc: 'Site Prep/Fencing/Etc.',
    dc_bos: 'DC Balance-of-System',
    dc_design_geotech_eng: 'DC Design',
    dc_installation: 'DC Installation',
};
export const acCostCategoryLabelMap = {
    ac_collection: 'AC Collection',
    // backward compatibility
    project_substation: 'Project Substation',
    mpt_and_breakers: 'MPT and Breakers',
    utility_poi_switchyard: 'Utility/POI Switchyard',
    gen_tie: 'Gen Tie',
    monitoring_scada_security: 'Monitoring',
    commissioning: 'Commissioning',
    o_and_m_building: 'O&M Building',
    testing: 'Testing',
};
export const miscCostCategoryLabelMap = {
    engineering_consultants: 'Engineering Consultants',
    epc_project_management: 'EPC Project Management',
    bn_project_management: 'Bn Project Management',
    bn_contingency: 'bn Contingency',
    epc_contingency: 'EPC Contingency',
    prevailing_wage: 'Prevailing Wage Adder',
    testing_commissioning: 'Testing & Commissioning',
};
export const costCategoriesLabelMap = {
    ...dcCostCategoryLabelMap,
    ...acCostCategoryLabelMap,
    ...miscCostCategoryLabelMap,
};
export const trackerMap = {
    gfw: 'Ground Fixed Tilt',
    ew: 'East West',
    tracker1p: 'Tracker 1P',
    tracker1p_terrain_following: 'Tracker 1P Terrain Following',
    tracker2p: 'Tracker 2P',
    floating: 'Floating',
    erthos: 'Erthos',
};
export const inverterTypes = ['central', 'string'];
export const moduleTechMap = {
    mono_perc: 'MonoPERC mono',
    mono_perc_bifi_glass_backsheet_framed: 'MonoPERC bifacial',
    n_top_con_bifi_glass_backsheet_framed: 'TopCon',
    n_hjt_bifi_glass_backsheet_framed: 'HJT',
};
const dcCostItemLabelMap = {
    pv_modules: 'PV Modules',
    trackers: 'Trackers',
    string_combiner_boxes: 'DC Equipment (Combiner/LBD) Supply',
    module_installation: 'Module Installation',
    installation_of_trackers: 'Installation of Trackers',
    installation_of_post: 'Installation of Posts',
    electrical_design_eng_consultants: 'Electrical Design/Eng Consultants',
    civil_environment_geotech: 'Civil Engineering',
    dc_bos_installation: 'DC System Install',
    dc_supply_material: 'DC Supply Materials',
    ac_installation: 'AC Installation',
    ac_supply_material: 'AC Supply Materials',
    epc_qc_qa_labor: 'EPC QA/QC Labor',
    site_building_om: 'O&M Site Building',
    posts: 'Posts',
    concrete_inverter_xfmr_pad_install: 'Concrete Inverter/Xfmr Pad Install',
    testing: 'Testing',
    trenching_and_directional_boring: 'Trenching and Directional Boring',
    commissioning: 'Commissioning',
    site_prep_work: 'Site Prep Work',
    perimeter_fence: 'Perimeter Fence',
    epc_construction_management: 'EPC Construction Management',
    gen_tie: 'Gen Tie',
    monitoring_and_controls_supply: 'Monitoring and Controls Supply',
    monitoring_and_controls_install: 'Monitoring and Controls Install',
    security_camera_supply_install: 'Security Camera Supply/Install',
    dc_ag_cable_procurement: 'DC AG Cable Procurement',
    dc_ug_cable_procurement: 'DC UG Cable Procurement',
    construction_permitting: 'Construction Permitting',
    plant_structural_engineering: 'PV Plant Structural/Mechanical Engineering',
    plant_electrical_engineering: 'PV Plant Electrical Engineering',
    laydown_areas: 'Laydown Areas',
    state_local_taxes: 'State & Local Taxes',
    liability_insurance: 'Liability Insurance',
    builders_all_risk_insurance: 'Builders All-Risk Insurance',
    contractor_performance_security: 'Contractor Performance Security',
    met_stations_supply_install: 'MET Stations Supply/Install',
    bn_construction_permits: 'bn Construction Permits',
    telecom_fiber_communication_utility: 'Telecom Fiber Communication Utility',
    general_owner_contingency: 'General Owner Contingency',
    construction_water_reserve: 'Construction Water Reserve',
    subsurface_reserve: 'Subsurface Reserve',
    tariff_reserve: 'Tariff Reserve',
    om_spare_parts: 'Spare Parts for O&M - Owner Hardware',
};
const byInverterCountCostItemLabelMap = {
    inverters_mv_stations: 'PV Inverter/MVT/PCS Supply',
    pv_controls_and_comms_supply: 'PV Controls and Communication Supply',
    mv_ac_collection_misc_procurement: 'MV AC Collection Misc Procurement',
    pv_controls_and_comms_install: 'PV Controls and Communication Install',
    inverter_install: 'PV Inverter/MVT/PCS Install',
    ac_system_install: 'AC System Install',
    testing_commissioning: 'PV Testing & Commissioning',
};
const mvarCostItemLabelMap = {
    // backward compatibility
    capacitor_banks: 'Capacitor Banks',
};
const acPoiCostItemLabelMap = {
    substation_materials: 'Substation Materials',
    substation_construction: 'Substation Construction',
    capacitor_banks: 'Capacitor Banks',
    // priced per construction period as bn_construction_mgmt since PVCosting 3.4
    construction_management: 'bn Construction Management (legacy, per MW-ac POI)',
    vp_project_operations: 'bn VP Project Operations',
    budgeting_scheduling: 'bn Budgeting Scheduling',
    owner_engineer: 'bn Owner Engineer',
    mpt_and_breakers: 'MPT & Breakers',
    utility_poi_switchyard: 'Utility/POI Switchyard',
};
const moduleCostItemLabelMap = {
    pile_supply: 'Pile Supply',
    racking_supply: 'Racking Supply',
    pile_install: 'Pile Install',
    racking_install: 'Racking Install',
    module_install: 'Module Install',
    module_wire_mgmt: 'Module Wire Management',
    // backward compatibility
    dc_cables_module_to_scb: 'DC Cables Module to SCB',
    dc_cables_scb_to_inverter: 'DC Cables SCB to Inverter',
};
const stringCostItemLabelMap = {
    line_connectors_mc_4: 'Line Connectors MC4',
};
const byAcreCostItemLabelMap = {
    timber_clearing: 'Clearing and Grubbing',
    surveying: 'Surveying',
    swppp_design: 'SWPPP Design',
    site_studies: 'Site Studies',
    grading_and_ground_preparation: 'Grading and Ground Preparation',
    erosion_control: 'Erosion Control - SWPPP',
    dust_control: 'Construction Water and Dust Control',
    access_roads: 'Access Roads',
    seeding_groundcover: 'Seeding & Groundcover',
    trenching_and_boring: 'Trenching & Boring',
};
const byGenTieLengthCostItemLabelMap = {
    gen_tie_engineering: 'Gentie Engineering',
    // for backward compatibility
    gen_tie_materials: 'Gen Tie Materials',
    gen_tie_construction: 'Gen Tie Construction',
    gen_tie: 'Gen Tie',
};
const byMptCountCostItemLabelMap = { substation_engineering: 'Substation Engineering' };
const byConstructionPeriodCostItemLabelMap = {
    project_construction_mgmt: 'Project & Construction Management',
    temp_site_security: 'Temp Site Security',
    temp_construction_facilities: 'Temp Construction Facilities',
    bn_construction_mgmt: 'bn Construction Management',
};
const byLandscapingMilesCostItemLabelMap = {
    landscaping: 'Landscaping',
};
const byPerimeterCostItemLabelMap = {
    fencing: 'Fencing',
};
export const byCableLengthCostItemLabelMap = {
    cable_1_0: '1/0 Cable',
    cable_4_0: '4/0 Cable',
    cable_500_kcmil: '500 kcmil Cable',
    cable_750_kcmil: '750 kcmil Cable',
    cable_1000_kcmil: '1000 kcmil Cable',
    cable_1250_kcmil: '1250 kcmil Cable',
};
export const byTotalCableLengthCostItemLabelMap = {
    trench_ground: 'Trench Ground',
    fiber_optic: 'Fiber Optic',
};
export const cableOptions = objectKeys(byCableLengthCostItemLabelMap);
const fixedCostItems = {
    spcc_design: 'SPCC Package',
    om_design: 'O&M Design',
    mobilization: 'Mobilization',
    demolition: 'Demolition',
    public_road_upgrades: 'Public/County Road Upgrades',
    om_building: 'O&M Building',
    permanent_security_system_supply_install: 'Permanent Security System Supply/Install',
    bn_owner_engineer: 'bn Owner Engineer',
};
export const dcOAndMCostItemLabelMap = {
    o_and_m_pv: 'O&M PV',
    panel_washing: 'Panel Washing',
    vegetation_management: 'Vegetation Management',
    mowing: 'Mowing',
    insurance_pv: 'PV Insurance',
    asset_management: 'Asset Management',
    staffing_and_facilities: 'Staffing & Facilities',
    replacement_parts: 'Replacement Parts',
    professional_services: 'Professional Services',
    vegetative_screening_and_railroad_gentie_costs: 'Vegetative Screening and Railroad Gentie Costs',
    prevailing_wage: 'Prevailing Wage Adder',
};
export const costItemsLabelMap = {
    ...dcCostItemLabelMap,
    ...byInverterCountCostItemLabelMap,
    ...acPoiCostItemLabelMap,
    ...moduleCostItemLabelMap,
    ...stringCostItemLabelMap,
    ...byAcreCostItemLabelMap,
    ...byGenTieLengthCostItemLabelMap,
    ...byMptCountCostItemLabelMap,
    ...byConstructionPeriodCostItemLabelMap,
    ...byLandscapingMilesCostItemLabelMap,
    ...byPerimeterCostItemLabelMap,
    ...byCableLengthCostItemLabelMap,
    ...byTotalCableLengthCostItemLabelMap,
    ...mvarCostItemLabelMap,
    ...fixedCostItems,
    ...dcOAndMCostItemLabelMap,
    epc_contingency: 'EPC Contingency',
    prevailing_wage: 'Prevailing Wage Adder',
};
export const baseCapexUnits = [
    'by_dc_capacity',
    'by_inverter_count',
    'by_ac_inverter_capacity',
    'by_mvar_capacity',
    'by_ac_poi_capacity',
    'by_module',
    'by_string',
    'by_acre',
    'by_gen_tie_length',
    'by_mpt_count',
    'by_cable_length',
    'by_construction_period',
    'by_landscaping_miles',
    'by_perimeter',
    'fixed_costs',
];
export const opexUnitOptions = ['by_dc_capacity', 'by_module', 'by_acre'];
const baseBomCapexSchema = z.strictObject({
    by_dc_capacity: z.array(genericCostSchema),
    by_ac_inverter_capacity: z.array(genericCostSchema),
    by_inverter_count: z.array(genericCostSchema).optional(),
    by_module: z.array(genericCostSchema),
    by_string: z.array(genericCostSchema),
    by_acre: z.array(genericCostSchema),
    by_gen_tie_length: z.array(genericCostSchema),
    by_mpt_count: z.array(genericCostSchema).optional(),
    by_construction_period: z.array(genericCostSchema).optional(),
    by_cable_length: z.array(genericCostSchema).optional(),
    by_landscaping_miles: z.array(genericCostSchema).optional(),
    by_perimeter: z.array(genericCostSchema).optional(),
    fixed_costs: z.array(genericCostSchema).optional(),
    // for backward compatibility
    by_mvar_capacity: z.array(genericCostSchema).optional(),
    by_ac_poi_capacity: z.array(genericCostSchema).optional(),
});
const opexCostSchema = genericCostSchema.extend({
    category: z.string().nullable().optional(),
});
const baseBomOpexSchema = z.object({
    by_dc_capacity: z.array(opexCostSchema),
    by_module: z.array(opexCostSchema),
    by_acre: z.array(opexCostSchema),
});
const acCableLengthSchema = z.object({
    length: z.number().min(0).max(1e6),
    multiplier: z.number().min(0).default(1),
});
const basePvCostingRequestSchema = z.object({
    cod: z.iso.datetime(),
    discount_rate: z.number().optional(),
    om_growth_rate: z.number().optional(),
    contracted_term: z.int().min(0).max(50),
    merchant_tail_term: z.int().min(0).max(50),
    land: z.object({
        purchase_bn_controlled_acreage: z.number().min(0).optional(),
        lease_bn_controlled_acreage: z.number().min(0).optional(),
        purchase_buildable_area_percentage: z.number().min(0).max(1).optional(),
        lease_buildable_area_percentage: z.number().min(0).max(1).optional(),
        lease_cost: z.number().min(0).max(100e3),
        purchase_cost: z.number().min(0).max(500e3),
        surface_multiplier: z.number().min(0.9).max(5),
        landscaping_miles: z.number().min(0).max(1e3).optional(),
        perimeter_ft: z.number().min(0).max(100e6).optional(),
        min_lease_acreage: z.number().min(0).default(0),
        fence_perimeter_multiplier: z.number().min(0).default(1),
        design_acreage: z.number().min(0).max(100e3).optional(),
    }),
    transmission: z.object({
        miles_to_ic: z.number().min(0).max(100),
        ic_voltage_kv: z.number().min(0).max(500),
    }),
    bom: z.object({
        capex: baseBomCapexSchema
            .extend({
            discretionary_costs: z.array(getDiscretionaryCostSchema(baseCapexUnits)).optional(),
            hv: hvCostingSchema.nullable().optional(),
        })
            .strict(),
        opex: baseBomOpexSchema
            .extend({
            discretionary_costs: z.array(getDiscretionaryCostSchema(opexUnitOptions)).optional(),
        })
            .strict(),
        construction_period_fn: z.custom().optional(), // legacy field
    }),
    tracker: z.enum(objectKeys(trackerMap)),
    module_tech: z.enum(objectKeys(moduleTechMap)),
    inverter_type: z.enum(inverterTypes),
    epc_contingency: z.number().min(0).max(0.2),
    num_panel_washes_per_year: z.int().min(0).max(12),
    num_mowings_per_year: z.int().min(0).max(12),
    perc_timber_clearing_required: z.number().min(0).max(1),
    perc_prevailing_wage_multiplier: z.number().min(0).max(1).optional(),
    prevailing_wage_cost_adder: z.number().min(0).optional(),
    opex_prevailing_wage_cost_adder: z.number().min(0).optional(),
    perc_module_spares: z.number().min(0).max(0.2).optional(),
    equipment_spend_schedule: capexSpendScheduleListSchema.nullable().optional(),
    epc_spend_schedule: capexSpendScheduleListSchema.nullable().optional(),
    cabling: z
        .object({
        ac: z.object({
            lengths: z.record(z.enum(objectKeys(byCableLengthCostItemLabelMap)), acCableLengthSchema),
            num_feeders: z.int().min(0).max(100),
        }),
    })
        .optional(),
    simulation: z
        .object({
        capex_discount: z.boolean().optional(),
    })
        .optional(),
});
export const pvCostingRequestSchema = basePvCostingRequestSchema
    .extend({
    pvsim_config: z.object({ id: compoundVersionIdSchemaZ }),
    databases: z.object({
        hv_cost_database: componentIdSchema,
        cost_database: componentIdSchema,
    }),
})
    .strict();
export const newPvConfigPayload = formNewConfigPayload(pvCostingRequestSchema);
export function getDevAcreage(inputs) {
    return ((inputs?.land.purchase_bn_controlled_acreage ?? 0) +
        (inputs?.land.lease_bn_controlled_acreage ?? 0));
}
export function getPurchaseCost(result, inputs) {
    return (Number(result?.land.purchase_design_acreage ?? 0) * Number(inputs?.land.purchase_cost ?? 0));
}
export function getDesignAcreage(result) {
    return (result?.land.purchase_design_acreage ?? 0) + (result?.land.lease_design_acreage ?? 0);
}
export function getBuildablePurchaseAcreage(inputs) {
    return ((inputs?.land.purchase_buildable_area_percentage ?? 0) *
        (inputs?.land.purchase_bn_controlled_acreage ?? 1.0));
}
export function getBuildableLeaseAcreage(inputs) {
    return ((inputs?.land.lease_buildable_area_percentage ?? 0) *
        (inputs?.land.lease_bn_controlled_acreage ?? 1.0));
}
export function getBuildableAcreage(result, inputs) {
    if (!result)
        return undefined;
    return getBuildablePurchaseAcreage(inputs) + getBuildableLeaseAcreage(inputs);
}
export const capexSectionLabelMap = {
    dc_epc_costs: 'DC EPC Costs',
    ac_epc_costs: 'AC EPC Costs',
    misc_epc_costs: 'Misc EPC Costs',
    hv_epc_costs: 'HV EPC Costs',
};
export const opexSectionLabelMap = {
    opex: 'First Year Opex Costs',
};
export const capexSections = objectKeys(capexSectionLabelMap);
//# sourceMappingURL=pv-costing.js.map